import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from './Dashboard';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  FileText, Upload, Search, Filter, Eye, Download, Trash2, Plus,
  Loader2, FolderOpen, Clock, User, Tag, Copy, ChevronLeft, ChevronRight,
  FileSpreadsheet, FileImage, File
} from 'lucide-react';
import { toast } from 'sonner';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  'Contract': FileText,
  'NDA': FileText,
  'Memo': FileText,
  'Invoice': FileSpreadsheet,
  'Agreement': FileText,
  'Letter': FileText,
  'Policy': FileText,
  'Report': FileSpreadsheet,
  'Proposal': FileText,
  'Other': File,
};

const CATEGORY_COLORS = {
  'Contract': 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  'NDA': 'text-red-400 bg-red-500/10 border-red-500/20',
  'Memo': 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  'Invoice': 'text-green-400 bg-green-500/10 border-green-500/20',
  'Agreement': 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  'Letter': 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
  'Policy': 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  'Report': 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  'Proposal': 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
  'Other': 'text-white/40 bg-white/5 border-white/10',
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const TemplatesPage = () => {
  const { user, token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [useDialogOpen, setUseDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const canManage = ['super_admin', 'admin', 'manager'].includes(user?.role);
  const authHeaders = () => ({ ...(token ? { Authorization: `Bearer ${token}` } : {}) });

  const fetchTemplates = async () => {
    try {
      const url = selectedCategory !== 'all'
        ? `${API_URL}/api/templates?category=${selectedCategory}`
        : `${API_URL}/api/templates`;
      const res = await fetch(url, { credentials: 'include', headers: authHeaders() });
      if (res.ok) setTemplates(await res.json());
    } catch { console.error('Failed to fetch templates'); }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/templates/categories`, { credentials: 'include', headers: authHeaders() });
      if (res.ok) setCategories(await res.json());
    } catch {}
  };

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchTemplates(); }, [selectedCategory, token]);

  const handlePreview = async (template) => {
    setSelectedTemplate(template);
    setPreviewLoading(true);
    setPreviewDialogOpen(true);
    try {
      const res = await fetch(`${API_URL}/api/templates/${template.template_id}/preview`, { credentials: 'include', headers: authHeaders() });
      if (res.ok) setPreviewContent(await res.json());
      else toast.error('Failed to load preview');
    } catch { toast.error('Preview failed'); }
    setPreviewLoading(false);
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/templates/${template.template_id}`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders()
      });
      if (res.ok) { toast.success('Template deleted'); fetchTemplates(); }
      else { const err = await res.json(); toast.error(err.detail || 'Delete failed'); }
    } catch { toast.error('Delete failed'); }
  };

  const filteredTemplates = templates.filter(t =>
    !searchQuery || t.name.toLowerCase().includes(searchQuery.toLowerCase()) || t.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout title="Document Templates">
      <div className="space-y-6" data-testid="templates-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <p className="text-white/60 text-sm">Browse and use pre-made document templates</p>
          </div>
          {canManage && (
            <Button onClick={() => setUploadDialogOpen(true)} className="bg-primary text-black hover:bg-primary/90" data-testid="upload-template-btn">
              <Plus className="w-4 h-4 mr-2" /> New Template
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..." className="pl-10 bg-black/40 border-white/10 text-white"
              data-testid="template-search-input"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <button onClick={() => setSelectedCategory('all')}
              className={`px-3 py-1.5 rounded-sm text-xs font-medium whitespace-nowrap transition-colors ${selectedCategory === 'all' ? 'bg-primary text-black' : 'bg-white/5 text-white/60 hover:text-white border border-white/10'}`}
              data-testid="filter-all">
              All
            </button>
            {categories.map(cat => (
              <button key={cat} onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-sm text-xs font-medium whitespace-nowrap transition-colors ${selectedCategory === cat ? 'bg-primary text-black' : 'bg-white/5 text-white/60 hover:text-white border border-white/10'}`}
                data-testid={`filter-${cat.toLowerCase()}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Template Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-16 text-white/40">
            <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-lg font-medium">No templates found</p>
            <p className="text-sm mt-1">{canManage ? 'Upload your first template to get started' : 'Templates will appear here once an admin uploads them'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="template-grid">
            {filteredTemplates.map(template => {
              const Icon = CATEGORY_ICONS[template.category] || File;
              const colorClass = CATEGORY_COLORS[template.category] || CATEGORY_COLORS['Other'];
              return (
                <div key={template.template_id} className="bg-black/40 border border-white/10 rounded-sm p-4 hover:border-white/20 transition-all group" data-testid={`template-card-${template.template_id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`p-2.5 rounded-sm border ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium text-sm truncate">{template.name}</h3>
                      <span className={`inline-block text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm mt-1 ${colorClass}`}>{template.category}</span>
                    </div>
                  </div>
                  {template.description && (
                    <p className="text-white/40 text-xs line-clamp-2 mb-3">{template.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-white/30 text-[10px] mb-3">
                    <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{formatFileSize(template.file_size)}</span>
                    <span className="flex items-center gap-1"><Copy className="w-3 h-3" />{template.usage_count || 0} uses</span>
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{template.created_by_name}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handlePreview(template)}
                      className="flex-1 h-8 text-xs border-white/10 text-white/70 hover:bg-white/5" data-testid={`preview-${template.template_id}`}>
                      <Eye className="w-3 h-3 mr-1" /> Preview
                    </Button>
                    <Button size="sm" onClick={() => { setSelectedTemplate(template); setUseDialogOpen(true); }}
                      className="flex-1 h-8 text-xs bg-primary text-black hover:bg-primary/90" data-testid={`use-${template.template_id}`}>
                      <Copy className="w-3 h-3 mr-1" /> Use
                    </Button>
                    {canManage && (
                      <Button size="sm" variant="outline" onClick={() => handleDelete(template)}
                        className="h-8 px-2 border-red-500/20 text-red-400 hover:bg-red-500/10" data-testid={`delete-${template.template_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload Template Dialog */}
      <UploadTemplateDialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen} categories={categories} token={token} onSuccess={() => { fetchTemplates(); setUploadDialogOpen(false); }} />

      {/* Preview Dialog */}
      <PreviewTemplateDialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen} template={selectedTemplate} content={previewContent} loading={previewLoading} />

      {/* Use Template Dialog */}
      <UseTemplateDialog open={useDialogOpen} onOpenChange={setUseDialogOpen} template={selectedTemplate} token={token} onSuccess={() => { setUseDialogOpen(false); toast.success('Document created! Go to My Documents to see it.'); }} />
    </DashboardLayout>
  );
};

// ===== Upload Template Dialog =====
const UploadTemplateDialog = ({ open, onOpenChange, categories, token, onSuccess }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Other');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!file || !name.trim()) return;
    setUploading(true);
    try {
      // Use base64 upload for reliability
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        const res = await fetch(`${API_URL}/api/templates/upload-base64`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({
            name: name.trim(), description: description.trim(), category,
            file_content: base64, file_type: file.type, file_name: file.name
          })
        });
        if (res.ok) { toast.success('Template uploaded!'); onSuccess(); setName(''); setDescription(''); setFile(null); setCategory('Other'); }
        else { const err = await res.json(); toast.error(err.detail || 'Upload failed'); }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch { toast.error('Upload failed'); setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><Upload className="w-5 h-5 text-primary" /> Upload Template</DialogTitle>
          <DialogDescription className="text-white/60">Upload a document to use as a reusable template</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-white/70">Template Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard NDA"
              className="bg-black/50 border-white/10 text-white" data-testid="template-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-white/70">Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description..."
              className="bg-black/50 border-white/10 text-white" data-testid="template-description-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-white/70">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="template-category-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {categories.map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-white/70">File</Label>
            <div className="border border-dashed border-white/20 rounded-sm p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileRef.current?.click()} data-testid="template-file-drop">
              {file ? (
                <div className="flex items-center gap-2 justify-center">
                  <FileText className="w-5 h-5 text-primary" />
                  <span className="text-white text-sm">{file.name}</span>
                  <span className="text-white/30 text-xs">({formatFileSize(file.size)})</span>
                </div>
              ) : (
                <div>
                  <Upload className="w-6 h-6 text-white/30 mx-auto mb-1" />
                  <p className="text-white/40 text-xs">Click to select file (PDF, Word, etc.)</p>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="template-file-input" />
          </div>
          <Button onClick={handleUpload} disabled={uploading || !name.trim() || !file}
            className="w-full bg-primary text-black hover:bg-primary/90" data-testid="template-upload-submit">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading...' : 'Upload Template'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ===== Preview Template Dialog =====
const PreviewTemplateDialog = ({ open, onOpenChange, template, content, loading }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (content?.content && content.file_type === 'application/pdf') {
      const binary = atob(content.content);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPdfUrl(null);
    }
  }, [content]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><Eye className="w-5 h-5 text-primary" /> {template?.name}</DialogTitle>
          <DialogDescription className="text-white/60">{template?.description || 'Template preview'}</DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
          ) : pdfUrl ? (
            <div>
              {numPages > 1 && (
                <div className="flex items-center justify-center gap-2 py-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage <= 1} className="text-white/80 h-7">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-white/60 text-xs">Page {currentPage} / {numPages}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))} disabled={currentPage >= numPages} className="text-white/80 h-7">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
              <div ref={containerRef} className="bg-white rounded-sm overflow-auto max-h-[60vh]" data-testid="template-preview-area">
                <Document file={pdfUrl} onLoadSuccess={({ numPages: np }) => setNumPages(np)}
                  loading={<div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>}
                  error={<div className="text-red-500 text-center p-4">Failed to load PDF</div>}>
                  <Page pageNumber={currentPage} renderTextLayer={false} renderAnnotationLayer={false}
                    width={containerRef.current?.getBoundingClientRect().width || 600} />
                </Document>
              </div>
            </div>
          ) : content?.content && content.file_type?.startsWith('image/') ? (
            <div className="bg-white rounded-sm p-2 max-h-[60vh] overflow-auto">
              <img src={`data:${content.file_type};base64,${content.content}`} alt={template?.name} className="w-full h-auto" />
            </div>
          ) : (
            <div className="text-white/40 text-center py-8">Preview not available for this file type</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ===== Use Template Dialog =====
const UseTemplateDialog = ({ open, onOpenChange, template, token, onSuccess }) => {
  const [docName, setDocName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (template) setDocName(`${template.name} - Copy`);
  }, [template]);

  const handleCreate = async () => {
    if (!docName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/templates/${template.template_id}/use`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ name: docName.trim() })
      });
      if (res.ok) { onSuccess(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to create document'); }
    } catch { toast.error('Failed'); }
    setCreating(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2"><Copy className="w-5 h-5 text-primary" /> Use Template</DialogTitle>
          <DialogDescription className="text-white/60">Create a new document from "{template?.name}"</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-white/70">Document Name</Label>
            <Input value={docName} onChange={(e) => setDocName(e.target.value)} className="bg-black/50 border-white/10 text-white" data-testid="use-template-name-input" />
          </div>
          <Button onClick={handleCreate} disabled={creating || !docName.trim()} className="w-full bg-primary text-black hover:bg-primary/90" data-testid="use-template-submit">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
            {creating ? 'Creating...' : 'Create Document'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplatesPage;
